<template>
  <div class="slide slide-key-metric">
    <!-- Decorative Rings -->
    <div class="rings-container">
      <div class="ring ring-outer"></div>
      <div class="ring ring-middle"></div>
      <div class="ring ring-inner"></div>
    </div>

    <!-- Content -->
    <div class="slide-content">
      <!-- Label -->
      <div class="metric-label-container">
        <span class="mono-label">{{ data.label }}</span>
      </div>

      <!-- Metric Value -->
      <div class="metric-value-container">
        <span class="metric-value" :class="scoreClass">
          {{ formatValue(data.value) }}
        </span>
      </div>

      <!-- Change Badge -->
      <div class="change-badge" :class="changeClass" v-if="data.change !== 0">
        <svg v-if="data.trend === 'up'" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 5L5 12H9V19H15V12H19L12 5Z" fill="currentColor"/>
        </svg>
        <svg v-else-if="data.trend === 'down'" class="trend-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 19L19 12H15V5H9V12H5L12 19Z" fill="currentColor"/>
        </svg>
        <span class="change-value">{{ formatChange(data.change) }}</span>
        <span class="change-label">{{ $t('reports.quarterly.key_metric.vs_last_quarter') }}</span>
      </div>

      <!-- Description -->
      <p class="metric-description" v-if="data.description">
        {{ data.description }}
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KeyMetricSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  computed: {
    scoreClass() {
      const value = this.data.value;
      const metricType = this.data.metricType || '';

      // eNPS (Employee Net Promoter Score): workplace recommendation
      if (metricType === 'enps' || (this.data.isNPS && this.data.label && this.data.label.toLowerCase().includes('employee'))) {
        if (value >= 50) return 'score-high';   // Excellent
        if (value >= 20) return 'score-medium'; // Good
        return 'score-low';                      // Needs attention
      }

      // NPS (Product/Service): internal product advocacy
      if (metricType === 'nps' || this.data.isNPS) {
        if (value >= 50) return 'score-high';   // Employees believe in products
        if (value >= 30) return 'score-medium'; // Moderate
        return 'score-low';                      // Poor advocacy
      }

      // WHO-5 Well-being Index (clinical thresholds)
      if (metricType === 'who5' || (this.data.label && this.data.label.toLowerCase().includes('who'))) {
        if (value >= 52) return 'score-high';   // Good well-being
        if (value >= 28) return 'score-medium'; // Moderate
        return 'score-low';                      // Concerning
      }

      // DEBI (Dynamic Engagement Behavior Index)
      if (metricType === 'debi') {
        if (value >= 60) return 'score-high';   // Elite
        if (value >= 40) return 'score-medium'; // Effective/Rising
        return 'score-low';                      // At Risk
      }

      // Default percentage-based scores
      if (value >= 70) return 'score-high';
      if (value >= 50) return 'score-medium';
      return 'score-low';
    },
    changeClass() {
      if (this.data.trend === 'up') return 'change-positive';
      if (this.data.trend === 'down') return 'change-negative';
      return 'change-neutral';
    }
  },
  methods: {
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatValue(value) {
      if (this.data.isNPS) {
        return value > 0 ? `+${this.formatNumber(value)}` : this.formatNumber(value);
      }
      // Raw numbers (scores, counts) don't get % suffix
      if (this.data.isRawNumber || this.data.isIndex) {
        return this.formatNumber(value);
      }
      return `${this.formatNumber(value)}%`;
    },
    formatChange(change) {
      const prefix = change > 0 ? '+' : '';
      return `${prefix}${this.formatNumber(change)}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-key-metric {
  @extend .slide-base;
  background: $paper;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.rings-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid rgba($violet-500, 0.15);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.ring-outer {
  width: 600px;
  height: 600px;
}

.ring-middle {
  width: 480px;
  height: 480px;
  border-color: rgba($violet-500, 0.1);
}

.ring-inner {
  width: 360px;
  height: 360px;
  border-color: rgba($violet-500, 0.05);
}

.slide-content {
  position: relative;
  z-index: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.metric-label-container {
  margin-bottom: 24px;

  .mono-label {
    @extend .mono-label;
    font-size: 16px;
    color: $ink-subtle;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
}

.metric-value-container {
  margin-bottom: 32px;
}

.metric-value {
  @extend .serif-headline;
  font-size: clamp(140px, 22vw, 280px);
  line-height: 1;

  &.score-high {
    color: $emerald;
  }

  &.score-medium {
    color: $amber;
  }

  &.score-low {
    color: $rose;
  }
}

.change-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 100px;
  margin-bottom: 40px;

  &.change-positive {
    background: rgba($emerald, 0.1);
    color: $emerald;
  }

  &.change-negative {
    background: rgba($rose, 0.1);
    color: $rose;
  }

  &.change-neutral {
    background: rgba($ink, 0.05);
    color: $ink-muted;
  }

  .trend-icon {
    width: 20px;
    height: 20px;
  }

  .change-value {
    font-family: $font-body;
    font-size: 18px;
    font-weight: 700;
  }

  .change-label {
    font-family: $font-body;
    font-size: 14px;
    opacity: 0.8;
  }
}

.metric-description {
  font-family: $font-body;
  font-size: 20px;
  color: $ink-muted;
  max-width: 600px;
  line-height: 1.6;
  margin: 0;
}
</style>
